// apps/web/src/api/russia-requests/items.ts
//
// Typed wrappers for the Russia SIMPLE position CRUD endpoints (#1545).
// Mirrors the China `api/requests/items.ts` layout —
// runtime validation is server-side, so the input shapes come straight
// from the shared Create/Patch item schemas.

import { z, type ZodType } from 'zod';
import {
  DraftPhotoCleanupResponseSchema,
  RussiaRequestItemSchema,
  type CreateRussiaRequestItemInput,
  type DraftPhotoCleanupRequest,
  type DraftPhotoCleanupResponse,
  type PatchRussiaRequestItemInput,
  type RussiaRequestItem,
} from '@magimp/shared';
import { request } from '../client.js';

/**
 * @aiPurpose POST /russia-requests/:id/items. Creates one SIMPLE-kind
 *   position on a DRAFT|RETURN request. Backend perm `russiaRequests.manage`.
 */
export function createRussiaRequestItem(
  requestId: string,
  input: CreateRussiaRequestItemInput,
): Promise<RussiaRequestItem> {
  return request<RussiaRequestItem>(
    `/russia-requests/${encodeURIComponent(requestId)}/items`,
    {
      method: 'POST',
      body: JSON.stringify(input),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RussiaRequestItemSchema as unknown as ZodType<RussiaRequestItem>,
  );
}

/**
 * @aiPurpose PATCH /russia-requests/:id/items/:itemId. Non-empty partial
 *   update of one position. Backend perm `russiaRequests.manage`.
 */
export function patchRussiaRequestItem(
  requestId: string,
  itemId: string,
  patch: PatchRussiaRequestItemInput,
): Promise<RussiaRequestItem> {
  return request<RussiaRequestItem>(
    `/russia-requests/${encodeURIComponent(requestId)}/items/${encodeURIComponent(itemId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(patch),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RussiaRequestItemSchema as unknown as ZodType<RussiaRequestItem>,
  );
}

/**
 * @aiPurpose DELETE /russia-requests/:id/items/:itemId. Hard-deletes one
 *   position; resolves on 204. Backend perm `russiaRequests.manage`.
 */
export function deleteRussiaRequestItem(requestId: string, itemId: string): Promise<void> {
  return request<void>(
    `/russia-requests/${encodeURIComponent(requestId)}/items/${encodeURIComponent(itemId)}`,
    { method: 'DELETE' },
    {},
    z.void(),
  );
}

/**
 * @aiPurpose POST /russia-requests/items/photo-cleanup — discard cleanup for
 *   never-saved position photo uploads when the user closes the editor before
 *   any position persisted. Authorization is conjunctive in the service
 *   (parsed key scope, actor-only namespace, no cross-capsule reference).
 *   Best-effort: per-key failures surface in `failed` without aborting the
 *   batch. Backend perm `russiaRequests.manage`. Mirror of China's
 *   `cleanupNeverSavedDraftPhotos`.
 */
export function cleanupNeverSavedPositionPhotos(
  body: DraftPhotoCleanupRequest,
): Promise<DraftPhotoCleanupResponse> {
  return request<DraftPhotoCleanupResponse>(
    '/russia-requests/items/photo-cleanup',
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    DraftPhotoCleanupResponseSchema,
  );
}
